// tslint:disable:no-console
import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";

import CliModule from "bpmn-js-cli";
import BpmnJsModeler from "bpmn-js/lib/Modeler";
import BpmnNavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import minimapModule from "diagram-js-minimap";

import { BatchUpdateElements } from "../actions/AddOrUpdateElement";
import { CopiedElement } from "../actions/PasteElements";

import * as ElementFactory from "../model/ElementFactory";
import FrendsElementTooltip from "../model/FrendsRules/FrendsElementTooltip";
import { FrendsRuleProvider, ProcessMode } from "../model/FrendsRules/FrendsRuleProvider";
import { ElementType, IElement, IElementDictionary, ITaskData } from "../model/IProcessData";
import { ValidationErrorDictionary } from "../model/ValidationData";

import customization from "../model/BpmnCustomization";
import customRenderer from "../model/CustomRenderer";

import ReduxFrendsElementStore from "../model/FrendsElementStore/ReduxFrendsElementStore";

import store from "../index";
import ruleset from "../model/FrendsRules";

import Canvas from "diagram-js/lib/core/Canvas";
import ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import { HashMap } from "../../shared/model/HashMap";
import { exportSVG } from "../model/CliExtensions/exportSVG";
import { exportXML } from "../model/CliExtensions/exportXML";
import { fireElementChanged } from "../model/CliExtensions/fireElementChanged";
import { importXml } from "../model/CliExtensions/importXml";
import { openMinimap } from "../model/CliExtensions/openMinimap";
import { toggleDefaultBranch } from "../model/CliExtensions/setDefaultBranch";
import { toggleStandardLoopCharacteristicsIcon } from "../model/CliExtensions/toggleStandardLoopCharacteristics";
import zoomScrollModule from "diagram-js/lib/navigation/zoomscroll";
import CustomModal from "shared/components/CustomModal";
import CanvasCustomControls from "shared/components/CanvasCustomControls";
import { ThemeType } from "shared/styles/theme";
import colors from "shared/styles/colors";
import Moddle from "moddle/lib/moddle";
import { ModdleElement } from "diagram-js/lib/model";
import { Attributes } from "diagram-js/lib/core/ElementFactory";
import ShapeCreateNameUpdateInterceptor from "../actions/BpmnShapeCreateNameUpdateInterceptor";

declare const cli: BpmnJsCli;

export class BpmnEditor extends React.Component<BpmnEditorProps, IBpmnEditorState> {
    private bpmnJs: BpmnJsModeler | BpmnNavigatedViewer;
    private errorMarker = "highlight-error-scope";
    private obsoleteMarker = "highlight-obsolete-task";
    private clipboard: Clipboard;
    private createdElementSourceMap: HashMap<string>; // Key is the ID of the new element while value is ID of the copied element
    private tooltip: FrendsElementTooltip;
    private drawnnMarkers: { [elementId: string]: string } = {};
    private defaultFillColor = this.props.theme === ThemeType.light ? colors.white : colors.dark5;
    constructor(props: BpmnEditorProps) {
        super(props);
        this.state = {
            showKeyboardShortcuts: false
        }
        this.bpmnJs = this.props.disableEdit ?
            new BpmnNavigatedViewer({
                keyboard: {
                    bindTo: document
                },
                bpmnRenderer: {
                    defaultFillColor: this.defaultFillColor
                },
                additionalModules: [customRenderer, zoomScrollModule, minimapModule, { frendsStore: ["type", ReduxFrendsElementStore] }]
            }) :
            new BpmnJsModeler({
                keyboard: {
                    bindTo: document
                },
                bpmnRenderer: {
                    defaultFillColor: this.defaultFillColor
                },
                additionalModules: [CliModule, customization, ShapeCreateNameUpdateInterceptor,
                    ruleset, customRenderer, minimapModule, zoomScrollModule,
                    { frendsStore: ["type", ReduxFrendsElementStore] }],
                cli: { bindTo: "cli" }
            });
    }

    public async componentDidMount() {
        this.bpmnJs.attachTo("#bpmn-editor-canvas");

        // Set process mode in the beginning so that BPMN can use this in the initialization
        if (this.props.isSubprocess) {
            FrendsRuleProvider.setProcessMode(ProcessMode.SubProcess);
        }

        if (!this.props.disableEdit) {
            cli._registerCommand("toggleDefaultBranch", toggleDefaultBranch);
            cli._registerCommand("toggleStandardLoopCharacteristicsIcon", toggleStandardLoopCharacteristicsIcon);
            cli._registerCommand("exportXML", exportXML);
            cli._registerCommand("exportSVG", exportSVG);
            cli._registerCommand("importXML", importXml);
            cli._registerCommand("openMinimap", openMinimap);
            cli._registerCommand("fireElementChanged", fireElementChanged);
        }

        const storeGraphPosition = () => {
            const state = store.getState();
            const graphPositionKey = `frends_process_viewbox_${String(state.process.id)}`;
            const viewbox = this.bpmnJs.get("canvas").viewbox();
            const viewboxJson = JSON.stringify(viewbox);
            localStorage.setItem(graphPositionKey, viewboxJson);
        }

        //Restores graph position if file in local storage exists
        const restoreGraphPosition = () => {
            const state = store.getState();
            const graphPositionKey = `frends_process_viewbox_${String(state.process.id)}`;
            const savedViewbox = localStorage.getItem(graphPositionKey);
            if (savedViewbox) {
                const viewbox = JSON.parse(savedViewbox);
                const canvas = this.bpmnJs.get("canvas")
                canvas.viewbox(viewbox);
            }
        }

        const setClipboard = (bpmnClipboard: any) => {
            const revivedCopy = JSON.parse(bpmnClipboard, this.createReviver(this.bpmnJs.get("moddle")));

            this.clipboard.set(revivedCopy);
        };

        const initModdleElementRecursively = (obj: any) => {
            if (!Object.prototype.hasOwnProperty.call(obj, "$type")) {
                return obj;
            }

            try {
                // moddle.getType() will throw an error if you have an invalid type, but you really should not..
                const ModdleElement = this.bpmnJs.get("moddle").getType(obj.$type);
                const moddleObj = new ModdleElement(obj);

                for (const propName in moddleObj) {
                    if (Object.prototype.hasOwnProperty.call(moddleObj, propName)) {
                        const prop = moddleObj[propName];
                        if (_.isArray(prop)) {
                            moddleObj[propName] = prop.map((p) => initModdleElementRecursively(p));
                        } else if (_.isObject(prop)) {
                            if (Object.prototype.hasOwnProperty.call(prop, "$type")) {
                                moddleObj[propName] = initModdleElementRecursively(prop);
                            }
                        }
                    }
                }

                return moddleObj;
            } catch (err) {
                console.error("Failed to initialize a ModdleElement from object ", obj, err);
                return obj;
            }
        };
        try {
            const { warnings } = await this.bpmnJs.importXML(this.props.bpmn);
            console.log(warnings)

        } catch (ex) {
            console.error(ex);
            this.props.onErrorMessage("Error when loading bpmn: " + ex.message);
        }

        const canvas = this.bpmnJs.get("canvas");
        // zoom to fit full viewport
        const zoomLevel: number = canvas.zoom("fit-viewport");
        // If default zoom is too small zoom to 50%
        // tslint:disable-next-line:no-unused-expression
        zoomLevel < 0.5 && canvas.zoom("0.5");

        if (!this.props.disableEdit) {
            this.clipboard = this.bpmnJs.get("clipboard") as Clipboard;
            this.tooltip = this.bpmnJs.get("frendsElementTooltip") as FrendsElementTooltip;
        }
        const eventBus = this.bpmnJs.get("eventBus");
        const bpmnClipboard = window.localStorage.getItem("bpmnElement");
        // Initialize clipboard from local storage if set:
        if (bpmnClipboard && this.clipboard) {
            try {
                setClipboard(bpmnClipboard);
            } catch (ex) {
                console.log("Setting clipboard from initial data failed. This might happen after a update. Clearing clipboard.");
                window.localStorage.removeItem("bpmnElement");
            }
        }
        eventBus.on("selection.changed", (event) => {
            const state = store.getState();
            if (event.newSelection.length === 1) {
                const selectedElementId = event.newSelection[0].id;
                // verify that the element is one we want to track
                const element = state.elements[selectedElementId] ? state.elements[selectedElementId] : null;
                this.props.onElementSelect(element);
            } else {
                // In case of multiple selected shapes or no selection at all, don't select anything.
                this.props.onElementSelect(null);
            }
        });

        window.onstorage = (event: StorageEvent) => {
            // If we change the local storage copy state from another tab or window, update the copy state:
            if (event.key === "bpmnElement" && this.clipboard) {
                setClipboard(event.newValue);
            }
        };

        eventBus.on(["elements.changed"], 0 as any, (event: EventBusElementsChangedEvent) => {
            const state = store.getState();
            // Lowest possible priority ensures all other listeners have processed the event before we get it.
            const batch = ElementFactory.createBatchUpdate(event.elements, state.elements);
            if (batch.added.length > 0 || batch.updated.length > 0 || batch.deleted.length > 0) {
                // Only trigger update action if there's actual elements to update
                this.props.onElementsBatchUpdate(batch);
            }
        });

        eventBus.on(["commandStack.changed"], 2000 as any, async (event: EventBusEvent) => {
            // Bump priority so other possible listeners do not get on the way;
            // e.g. context pad popup seems to register the listener once,
            // which blocks the default action, which means we do not get the saved XML to state

            // CommandStack.changed is triggered AFTER all events changing or removing shapes
            try {
                const { xml } = await this.bpmnJs.saveXML();
                this.props.onChange(xml);
            } catch (saveErr) {
                this.props.onErrorMessage("Error when saving bpmn: " + saveErr);
                console.error(saveErr);
            }
        });

        eventBus.on(["FRENDS.elements.imported"], 0 as any, (event: BpmnImportResult) => {
            if (!event.successful) {
                this.props.onErrorMessage(event.errorMessage);
            } else {
                const batch = ElementFactory.createBatchUpdate(event.elements, {} as IElementDictionary);
                // The state will be cleared, use an empty element dictionary in order to not merge unrelated elements
                this.props.onImport(batch);
                eventBus.fire("commandStack.changed", {});
            }
        });
        eventBus.on("copyPaste.pasteElements", 0, (event) => {
            this.createdElementSourceMap = {};
        });

        eventBus.on(["copyPaste.pasteElement"], 0 as any, (event: { descriptor: { id: string, businessObject: { id: string }, type: string } }) => {
            // Labels have the same event.descriptor.businessObject.id as the main element..
            // Newer bpmn label elements do not have a type 
            if (event.descriptor.type != undefined && event.descriptor.type !== "label") {
                this.createdElementSourceMap[event.descriptor.businessObject.id] = event.descriptor.id;
            }
        });

        eventBus.on(["commandStack.elements.create.postExecuted"], 0 as any, (event: { context: { elements: BpmnJsElement[], hints: any } }) => {
            // this hint identifies if this even is from a paste event
            // If not a paste event the whole property will be omitted
            // If is paste event the value will be false
            if (event?.context?.hints?.createElementsBehavior === false && event?.context?.elements != null) {
                const copiedParameterString = window.localStorage.getItem("elementParameters");
                const clipboardParameters = JSON.parse(copiedParameterString as string) as IElementDictionary;
                const result = ElementFactory.getCopiedElementParameters(event.context.elements, this.createdElementSourceMap, clipboardParameters);
                if (result.length > 0) {
                    this.props.onElementsPasted(result);
                }
            }
        });

        eventBus.on(["copyPaste.elementsCopied"], 0 as any, (event: EventBusCopyEvent) => {
            // Priority 0 for lowest possible priority. We want to let everything else have a chance to react to the event before we do.
            const result = this.clipboard.get();
            // Find the parameters to copy:
            const parameters = {} as IElementDictionary;
            const state = store.getState();
            for (const r in result) {
                if (Object.prototype.hasOwnProperty.call(result, r)) {
                    for (const i of result[r]) {
                        if (state.elements[i.id]) {
                            parameters[i.id] = state.elements[i.id];
                        }
                    }
                }
            }

            // Write the copy + parameters to local storage
            window.localStorage.setItem("bpmnElement", JSON.stringify(result));
            window.localStorage.setItem("elementParameters", JSON.stringify(parameters));
        });

        eventBus.on(["element.hover"], 0 as any, (event: EventBusEvent) => {
            const state = store.getState();
            if (event.element.type !== "bpmn:Task" && event.element.type !== "bpmn:CallActivity") {
                return;
            }
            if (state.elements[event.element.id] == null) {
                return;
            }
            if (!this.props.disableEdit) {
                this.tooltip.setTooltipMessage(state.elements[event.element.id].description, event.element);
            }
        });

        // DEBUG: uncomment for dumping all event invocations to console
        /*
        const events = Object.keys(eventBus._listeners);
        events.forEach((e) => {
            eventBus.on(e, (event: Event) => {
                console.log({ type: e, timestamp: new Date().getSeconds() + ":" + new Date().getMilliseconds(), event: event });
            });
        });
        */

        eventBus.on('canvas.viewbox.changed', function (event) {
            storeGraphPosition();
        });

        restoreGraphPosition();

        this.props.onLoaded();
    }

    public componentWillUnmount() {
        if (this.bpmnJs) {
            this.bpmnJs.destroy();
        }
    }

    // https://github.com/nikku/bpmn-js-copy-paste-example/blob/master/test/copy-paste.js#L169
    private createReviver(moddle: Moddle) {

        const elCache: HashMap<ModdleElement> = {};

        /**
         * The actual reviewer that creates model instances
         * for elements with a $type attribute.
         *
         * Elements with ids will be re-used, if already
         * created.
         *
         * @param  {String} key
         * @param  {Object} object
         *
         * @return {Object} actual element
         */
        return function (key: string, object: Attributes) {

            if (typeof object === 'object' && typeof object.$type === 'string') {

                const objectId = object.id;

                if (objectId && elCache[objectId]) {
                    return elCache[objectId];
                }

                const type = object.$type;
                const attrs = Object.assign({}, object);

                delete attrs.$type;

                const newEl = moddle.create(type, attrs);

                if (objectId) {
                    elCache[objectId] = newEl;
                }

                return newEl;
            }

            return object;
        };
    }

    public resetZoom = () => this.bpmnJs.get("zoomScroll").reset();

    public toggleMinimap = () => this.bpmnJs.get("minimap").toggle();

    public handleShowKeyboardShortcuts = (show: boolean) => this.setState({ showKeyboardShortcuts: show });
    public renderKeyboardShortcutsModal = () => {
        const shortcuts: Array<{ action: string, shortcut: string }> = [
            { action: "Undo", shortcut: `${ModKey.CtrlCmd} + Z` },
            { action: "Redo", shortcut: `${ModKey.CtrlCmd} + ${ModKey.Shift} + Z, ${ModKey.CtrlCmd} + Y` },
            { action: "Save as draft", shortcut: `${ModKey.CtrlCmd} + S` },
            { action: "Delete selected element", shortcut: "DEL" },
            { action: "Select all", shortcut: `${ModKey.CtrlCmd} + A` },
            { action: "Search", shortcut: `${ModKey.CtrlCmd} + F` },
            { action: "Scrolling (Vertical)", shortcut: "Scroll up/down" },
            { action: "Scrolling (Horizontal)", shortcut: `${ModKey.Shift} + Scroll up/down` },
            { action: "Zooming in", shortcut: `${ModKey.CtrlCmd} + Scroll up, ${ModKey.CtrlCmd} + +` },
            { action: "Zooming out", shortcut: `${ModKey.CtrlCmd} + Scroll down, ${ModKey.CtrlCmd} + -` },
            { action: "Reset zoom", shortcut: `${ModKey.CtrlCmd} + 0` },
            { action: "Reset zoom and position", shortcut: `${ModKey.CtrlCmd} + 9` },
            { action: "Edit selected element name", shortcut: "E" },
            { action: "Connect tool", shortcut: "C" },
            { action: "Hand tool", shortcut: "H" },
            { action: "Lasso tool", shortcut: `L, ${ModKey.Shift} + drag` },
            { action: "Space tool", shortcut: "S" }
        ]
        return (
            <CustomModal
                label="bpmn-keyboard-shortcuts"
                onClose={() => this.handleShowKeyboardShortcuts(false)}
                shouldCloseOnOverlayClick={true}
                show={true}
                modalTitle={<h2>Keyboard shortcuts</h2>}
            >
                {shortcuts.map((shortcut, i) => (
                    <S.FormGroup key={i}>
                        <S.FlexContainer justifySpaceBetween={true}>
                            <span>{shortcut.action}</span>
                            <span>{shortcut.shortcut}</span>
                        </S.FlexContainer>
                    </S.FormGroup>
                ))}
            </CustomModal>
        )
    };

    public render() {
        this.drawMarkers();
        return (
            <>
                <div id="bpmn-editor-canvas" style={{ height: "100%" }}></div>
                <CanvasCustomControls
                    onResetZoom={this.resetZoom}
                    onShowKeyboardShortcuts={() => this.handleShowKeyboardShortcuts(true)}
                    onToggleMinimap={this.toggleMinimap}
                />
                {this.state.showKeyboardShortcuts && this.renderKeyboardShortcutsModal()}
            </>
        );
    }



    // This function will run each time the bpmn editor is changed in any way.
    private drawMarkers() {
        if (!this.bpmnJs) { return; }
        const elementRegistry = this.bpmnJs.get("elementRegistry");
        const canvas = this.bpmnJs.get("canvas");
        // Clear all drawn markers.
        Object.keys(this.drawnnMarkers).forEach((elementId) => {
            const element = elementRegistry.get(elementId);
            // make sure the element still exists. parent = null for elements about to be removed.
            if (element && element.parent) {
                canvas.removeMarker(elementId, this.drawnnMarkers[elementId]);
            }
        });
        this.drawnnMarkers = {};
        this.drawValidationMarkers(elementRegistry, canvas);
        this.drawObsoleteTasks(elementRegistry, canvas);
    }

    private drawObsoleteTasks(elementRegistry: ElementRegistry, canvas: Canvas) {
        Object.keys(this.props.elementParameters).map((key) => {
            // Don't draw the obsolete marker over the validation marker
            if (this.props.elementParameters[key].type === ElementType.Task && !this.drawnnMarkers[key]) {

                const foundTask = this.props.tasks.find((task) => task.uri === this.props.elementParameters[key].selectedTypeId);

                if (foundTask != null && foundTask.isNotLatestVersion) {
                    const shape = elementRegistry.get(key);
                    // make sure the element still exists. parent = null for elements about to be removed.
                    if (shape && shape.parent) {
                        canvas.addMarker(key, this.obsoleteMarker);
                        this.drawnnMarkers[key] = this.obsoleteMarker;
                    }
                }
            }
        });
    }

    private drawValidationMarkers(elementRegistry: ElementRegistry, canvas: Canvas) {
        if (!this.props.validationErrorDictionary) { return; }
        for (const elementId in this.props.validationErrorDictionary) {
            if (Object.prototype.hasOwnProperty.call(this.props.validationErrorDictionary, elementId)) {
                const shape = elementRegistry.get(elementId);
                // make sure the element still exists. parent = null for elements about to be removed.
                if (shape && shape.parent) {
                    canvas.addMarker(elementId, this.errorMarker);
                    this.drawnnMarkers[elementId] = this.errorMarker;
                }
            }
        }
    }
}

enum ModKey {
    CtrlCmd = "Ctrl/CMD",
    Shift = "Shift"
}

interface IBpmnEditorState {
    showKeyboardShortcuts: boolean;
}

export interface BpmnEditorProps {
    bpmn: string;
    validationErrorDictionary: ValidationErrorDictionary | undefined;
    elementParameters: IElementDictionary;
    disableEdit?: boolean | undefined;
    tasks: ITaskData[];
    theme: ThemeType;
    onChange: (updatedBpmn: string) => void;
    onElementSelect: (element: IElement | null) => void;
    onElementsPasted: (pastedElements: CopiedElement[]) => void;
    onErrorMessage: (error: string | null) => void;
    onElementsBatchUpdate: (batch: BatchUpdateElements) => void;
    isSubprocess: boolean | undefined;
    onLoaded: () => void;
    onImport: (batch: BatchUpdateElements) => void;
}
